import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { isMarketingSite } from "@/utils/siteMode";
import { AuthProvider } from "@/contexts/AuthContext";
import { FoodLogProvider } from "@/contexts/FoodLogContext";
import { AppLayout } from "@/components/AppLayout";
import { PWAUpdatePrompt } from "@/components/PWAUpdatePrompt";
import { LoadingSpinner } from "@/components/LoadingSpinner";

// Lazy-load all pages including Index to reduce initial JS bundle
const Index = lazy(() => import("./pages/Index"));

// Lazy-load all other routes
const Welcome = lazy(() => import("./pages/Welcome"));
const NotFound = lazy(() => import("./pages/NotFound"));
const SubscriptionSuccess = lazy(() => import("./pages/SubscriptionSuccess"));
const Auth = lazy(() => import("./pages/Auth"));
const FoodDetail = lazy(() => import("./pages/FoodDetail"));
const FoodDiary = lazy(() => import("./pages/FoodDiary"));
const EncourageLogin = lazy(() => import("./pages/EncourageLogin"));
const Admin = lazy(() => import("./pages/Admin"));
const MyFoodCards = lazy(() => import("./pages/MyFoodCards"));
const Search = lazy(() => import("./pages/Search"));
const Meals = lazy(() => import("./pages/Meals"));
const Tracker = lazy(() => import("./pages/Tracker"));
const ArticleLibrary = lazy(() => import("./pages/ArticleLibrary"));
const ArticleDetail = lazy(() => import("./pages/ArticleDetail"));
const ContentManagement = lazy(() => import("./pages/ContentManagement"));
const MealDetail = lazy(() => import("./pages/MealDetail"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const Guide = lazy(() => import("./pages/Guide"));
const ShoppingList = lazy(() => import("./pages/ShoppingList"));
const CampaignLanding = lazy(() => import("./pages/CampaignLanding"));
const PremiumPage = lazy(() => import("./pages/Premium"));
const Contact = lazy(() => import("./pages/Contact"));
const MarketingLanding = lazy(() => import("./pages/MarketingLanding"));
const AcceptInvitation = lazy(() => import("./pages/AcceptInvitation"));

// SEO landing pages
const BLW = lazy(() => import("./pages/landing/BLW"));
const FastFodeBaby = lazy(() => import("./pages/landing/FastFodeBaby"));
const AgeLandingPages = () => import("./pages/landing/AgeLanding");
const Baby6 = lazy(() => AgeLandingPages().then(m => ({ default: m.Baby6 })));
const Baby7 = lazy(() => AgeLandingPages().then(m => ({ default: m.Baby7 })));
const Baby8 = lazy(() => AgeLandingPages().then(m => ({ default: m.Baby8 })));
const Baby9 = lazy(() => AgeLandingPages().then(m => ({ default: m.Baby9 })));
const Baby10 = lazy(() => AgeLandingPages().then(m => ({ default: m.Baby10 })));
const Baby11 = lazy(() => AgeLandingPages().then(m => ({ default: m.Baby11 })));
const Baby12 = lazy(() => AgeLandingPages().then(m => ({ default: m.Baby12 })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      gcTime: 15 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: 2,
    },
  },
});

const SuspenseFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <LoadingSpinner />
  </div>
);

const MARKETING = isMarketingSite();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <PWAUpdatePrompt />
        <Toaster />
        <Sonner />
        <BrowserRouter>
          {MARKETING ? (
            <Suspense fallback={<SuspenseFallback />}>
              <Routes>
                <Route path="/" element={<MarketingLanding />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/premium" element={<div className="min-h-screen bg-gradient-soft"><PremiumPage /></div>} />
                <Route path="/subscription-success" element={<SubscriptionSuccess />} />
                <Route path="/accept-invitation" element={<AcceptInvitation />} />
                <Route path="/kontakt" element={<Contact />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                <Route path="/blw" element={<BLW />} />
                <Route path="/fast-fode-baby" element={<FastFodeBaby />} />
                <Route path="/baby-6-maneder" element={<Baby6 />} />
                <Route path="/baby-7-maneder" element={<Baby7 />} />
                <Route path="/baby-8-maneder" element={<Baby8 />} />
                <Route path="/baby-9-maneder" element={<Baby9 />} />
                <Route path="/baby-10-maneder" element={<Baby10 />} />
                <Route path="/baby-11-maneder" element={<Baby11 />} />
                <Route path="/baby-12-maneder" element={<Baby12 />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Suspense>
          ) : (
          <FoodLogProvider>
            <Suspense fallback={<SuspenseFallback />}>
              <Routes>
                <Route path="/" element={<AppLayout><Index /></AppLayout>} />
                <Route path="/matkort" element={<AppLayout><MyFoodCards /></AppLayout>} />
                <Route path="/sok" element={<AppLayout><Search /></AppLayout>} />
                <Route path="/maltider" element={<AppLayout><Meals /></AppLayout>} />
                <Route path="/logg" element={<AppLayout><Tracker /></AppLayout>} />
                <Route path="/welcome" element={<Welcome />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/mat/:id" element={<AppLayout><FoodDetail /></AppLayout>} />
                <Route path="/baby/:id/diary" element={<AppLayout><FoodDiary /></AppLayout>} />
                <Route path="/encourage-login" element={<EncourageLogin />} />
                <Route path="/subscription-success" element={<SubscriptionSuccess />} />
                <Route path="/admin" element={<AppLayout><Admin /></AppLayout>} />
                <Route path="/content-management" element={<AppLayout><ContentManagement /></AppLayout>} />
                <Route path="/meal/:id" element={<AppLayout><MealDetail /></AppLayout>} />
                <Route path="/artikler" element={<AppLayout><ArticleLibrary /></AppLayout>} />
                <Route path="/artikkel/:id" element={<AppLayout><ArticleDetail /></AppLayout>} />
                <Route path="/matdagbok" element={<AppLayout><FoodDiary /></AppLayout>} />
                <Route path="/guide" element={<AppLayout><Guide /></AppLayout>} />
                <Route path="/handleliste" element={<AppLayout><ShoppingList /></AppLayout>} />
                <Route path="/campaign" element={<CampaignLanding />} />
                <Route path="/premium" element={<AppLayout><PremiumPage /></AppLayout>} />
                <Route path="/kontakt" element={<AppLayout><Contact /></AppLayout>} />
                <Route path="/accept-invitation" element={<AcceptInvitation />} />
                <Route path="/blw" element={<AppLayout><BLW /></AppLayout>} />
                <Route path="/fast-fode-baby" element={<AppLayout><FastFodeBaby /></AppLayout>} />
                <Route path="/baby-6-maneder" element={<AppLayout><Baby6 /></AppLayout>} />
                <Route path="/baby-7-maneder" element={<AppLayout><Baby7 /></AppLayout>} />
                <Route path="/baby-8-maneder" element={<AppLayout><Baby8 /></AppLayout>} />
                <Route path="/baby-9-maneder" element={<AppLayout><Baby9 /></AppLayout>} />
                <Route path="/baby-10-maneder" element={<AppLayout><Baby10 /></AppLayout>} />
                <Route path="/baby-11-maneder" element={<AppLayout><Baby11 /></AppLayout>} />
                <Route path="/baby-12-maneder" element={<AppLayout><Baby12 /></AppLayout>} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/terms-of-service" element={<TermsOfService />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </FoodLogProvider>
          )}
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
